// src/components/CreatorPage/CreatorPage.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useParams, Link as RouterLink } from 'react-router-dom';
import API from '../config';
import {
  Container,
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Avatar,
  Button,
  Grid,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Chip,
  Rating,
  useTheme,
  alpha,
} from '@mui/material';
import { ThumbUp, ThumbDown, OpenInNew } from '@mui/icons-material';

const spentMap = {
  '$0–25':    12.5,
  '$25–50':   37.5,
  '$50–100':  75,
  '$100–250': 175,
  '$250+':    250,
};

export default function CreatorPage() {
  const { id } = useParams();
  const theme = useTheme();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    axios.get(`${API}/api/creators/${id}`)
      .then(({ data }) => setCreator(data.creator))
      .catch(err => {
        console.error(err);
        setError('Failed to load creator details.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 8 }}>
        {error}
      </Typography>
    );
  }

  // summary stats
  const totalReviews   = creator.reviews.length;
  const recommendCount = creator.reviews.filter(r => r.recommend).length;
  const recommendPct   = totalReviews
    ? Math.round((recommendCount / totalReviews) * 100)
    : 0;

  const qualityValues = creator.reviews.map(r => r.contentQuality);
  const avgQuality    = qualityValues.length
    ? qualityValues.reduce((sum, v) => sum + v, 0) / qualityValues.length
    : 0;

  const spentValues = creator.reviews
    .map(r => spentMap[r.spent])
    .filter(v => typeof v === 'number');
  const avgSpent = spentValues.length
    ? spentValues.reduce((sum, v) => sum + v, 0) / spentValues.length
    : 0;
  const fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      {/* Banner & Avatar */}
      <Box sx={{ position: 'relative', mb: 8 }}>
        <Box
          sx={{
            height: { xs: 200, sm: 300 },
            backgroundImage: `url(${creator.bannerImage || '/default-banner.jpg'})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Avatar
          src={creator.profilePicture || '/default-profile.png'}
          alt={creator.username}
          sx={{
            width: 128,
            height: 128,
            border: '4px solid white',
            position: 'absolute',
            bottom: -64,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        />
      </Box>

      {/* Profile Details */}
      <Card sx={{ mb: 4, pt: 8 }}>
        <CardContent sx={{ textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {creator.username}
          </Typography>
          <Link
            href={creator.profile_url}
            target="_blank"
            rel="noopener"
            underline="none"
          >
            <Button variant="outlined" endIcon={<OpenInNew />}>
              View My OF
            </Button>
          </Link>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {creator.bio || 'No bio available'}
          </Typography>

          <Grid container spacing={2} justifyContent="center" sx={{ mt: 2 }}>
            <Grid item>
              <Typography>
                <strong>Average Quality:</strong>{' '}
                { totalReviews ? `${avgQuality.toFixed(1)} / 5` : 'N/A' }
              </Typography>
            </Grid>
            <Grid item>
              <Typography sx={{ display: 'inline-flex', alignItems: 'center' }}>
                <strong>Recommend:</strong>&nbsp;{recommendPct}%&nbsp;
                {recommendPct >= 50
                  ? <ThumbUp color="primary" />
                  : <ThumbDown color="error" />}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                <strong>Avg Spent:</strong>{' '}
                {avgSpent > 0 ? fmt.format(avgSpent) : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reviews List */}
      <Typography variant="h5" gutterBottom>
        Reviews
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box>
        {creator.reviews.length > 0 ? (
          <Grid container spacing={3} sx={{ display: 'flex', justifyContent: 'center' }}>
            {creator.reviews.map((review, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Card
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                      // Fixed dimensions for all cards
                      height: 380,
                      width: 320,
                      maxWidth: 320,
                    }}
                  >
                    {/* Reviewer info header */}
                    <CardHeader
                      avatar={
                        <Avatar
                          src={review.user?.profilePicture || '/default-profile.png'}
                          alt={review.user?.username || 'Anonymous'}
                          sx={{ 
                            width: 40, 
                            height: 40,
                            border: `2px solid ${theme.palette.background.paper}`,
                          }}
                        />
                      }
                      title={
                        <Typography variant="subtitle1" fontWeight={600} noWrap>
                          {review.user?.username || ''}
                        </Typography>
                      }
                      action={
                        <Box sx={{ width: 130, textAlign: 'center' }}>
                          <Chip
                            icon={review.recommend ? <ThumbUp fontSize="small" /> : <ThumbDown fontSize="small" />}
                            label={review.recommend ? 'Recommended' : 'Not Recommended'}
                            color={review.recommend ? 'success' : 'error'}
                            size="small"
                            sx={{ mt: 1, width: '100%' }}
                          />
                        </Box>
                      }
                      sx={{ padding: '8px 16px' }}
                    />
                    
                    <Divider />
                    
                    {/* Review content */}
                    <CardContent sx={{ flexGrow: 1, pt: 2, width: '100%', padding: '16px' }}>
                      {/* Rating */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          Quality:
                        </Typography>
                        <Rating
                          value={review.contentQuality}
                          precision={0.5}
                          size="small"
                          readOnly
                        />
                        <Typography variant="body2" fontWeight={500} sx={{ ml: 1 }}>
                          {review.contentQuality}/5
                        </Typography>
                      </Box>
                      
                      {/* Review text */}
                      {review.fullReview && (
                        <Box sx={{ mb: 2, width: 288, maxWidth: 288 }}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Review:
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontStyle: 'italic',
                              color: 'text.primary',
                              lineHeight: 1.6,
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              wordBreak: 'break-word',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              width: 288,
                              maxWidth: 288,
                            }}
                          >
                            "{review.fullReview}"
                          </Typography>
                        </Box>
                      )}
                      
                      {/* Key metrics */}
                      <Grid container spacing={1} sx={{ mt: 1, width: 288, maxWidth: 288 }}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary">
                              Spent
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {review.spent || 'N/A'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary">
                              Frequency
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {review.contentFrequency}/5
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary">
                              Authenticity
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {review.authenticity}/5
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="caption" color="text.secondary">
                              Engagement
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {review.engagement}/5
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                    
                    {/* Footer with date */}
                    <Box sx={{ p: 2, pt: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 288, maxWidth: 288 }}>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: '100%' }}>
                        {new Date(review.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </Card>
                </Grid>
            ))}
          </Grid>
        ) : (
          <Typography>No reviews available yet.</Typography>
        )}
      </Box>
    </Container>
  );
}
